
import React from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  createRoutesFromElements,
  Route,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'sonner';

import { AuthProvider } from './contexts/auth';
import ProtectedRoute from './components/ProtectedRoute';
import ApprovalStatusChecker from './components/auth/ApprovalStatusChecker';
import { useOneSignal } from './hooks/useOneSignal';
import { NotificationPermissionPrompt } from './components/common/NotificationPermissionPrompt';
import { useNotificationPrompt } from './hooks/useNotificationPrompt';
import { usePushNotificationWizard } from './hooks/usePushNotificationWizard';
import { PushNotificationWizard } from './components/common/PushNotificationWizard';
import AuthDebugInfo from './components/debug/AuthDebugInfo';
import Index from './pages/Index';
import DashboardPage from '@/pages/DashboardPage';
import PendingApprovalPage from '@/components/auth/PendingApprovalPage';
import ReagentsPage from './pages/ReagentsPage';
import MyReagentsPage from './pages/MyReagentsPage';
import NewUploadReagentPage from './pages/NewUploadReagentPage';
import ReagentDetailsPage from './pages/ReagentDetailsPage';
import EditReagentPage from './pages/EditReagentPage';
import RequestedReagentsPage from './pages/RequestedReagentsPage';
import RequestReagentPage from './pages/RequestReagentPage';
import SwapRequestPage from './pages/SwapRequestPage';
import ViewSwapRequestPage from './pages/ViewSwapRequestPage';
import ConversationPage from './pages/ConversationPage';
import MessagesPage from './pages/MessagesPage';
import AdminPage from './pages/AdminPage';
import ProfilePage from './pages/ProfilePage';
import PublicProfilePage from './pages/PublicProfilePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import NotFound from './pages/NotFound';
import MessagePage from './pages/MessagePage';
import CommunityPage from './pages/CommunityPage';
import HelpCenterPage from './pages/HelpCenterPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsAndConditionsPage from './pages/TermsAndConditionsPage';
import ContactPage from './pages/ContactPage';
import InstallAppDialog from './components/common/InstallAppDialog';
import { InstallPWAPrompt } from './components/pwa/InstallPWAPrompt';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => {
  useOneSignal(); // Initialize OneSignal
  const { shouldShowPrompt, dismissPrompt } = useNotificationPrompt();
  const { shouldShow: showWizard, handleComplete, handleDismiss } = usePushNotificationWizard();
  
  return (
    <div className="min-h-screen flex flex-col">
      {/* Add the InstallAppDialog component here to show for all users */}
      <InstallAppDialog />
      <InstallPWAPrompt />
      <AuthDebugInfo />
      {shouldShowPrompt && (
        <NotificationPermissionPrompt 
          onDismiss={dismissPrompt} 
          variant="banner"
          showDismissButton={true}
        />
      )}
      {showWizard && (
        <PushNotificationWizard 
          onComplete={handleComplete}
          onDismiss={handleDismiss}
        />
      )}
      <ApprovalStatusChecker>
        <Outlet />
      </ApprovalStatusChecker>
    </div>
  );
};

// Create router with all routes
const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    errorElement: <NotFound />,
    children: [
      {
        index: true,
        element: <Index />
      },
      {
        path: 'dashboard',
        element: <ProtectedRoute><DashboardPage /></ProtectedRoute>
      },
      {
        path: 'reagents',
        element: <ReagentsPage />
      },
      {
        path: 'reagents/new',
        element: <ProtectedRoute><NewUploadReagentPage /></ProtectedRoute>
      },
      {
        path: 'reagents/:id',
        element: <ReagentDetailsPage />
      },
      {
        path: 'reagents/:id/edit',
        element: <ProtectedRoute><EditReagentPage /></ProtectedRoute>
      },
      {
        path: 'reagents/edit/:id',
        element: <ProtectedRoute><EditReagentPage /></ProtectedRoute>
      },
      {
        path: 'reagent-requests',
        element: <ProtectedRoute><RequestedReagentsPage /></ProtectedRoute>
      },
      {
        path: 'reagent-requests/new',
        element: <ProtectedRoute><RequestReagentPage /></ProtectedRoute>
      },
      {
        path: 'request-reagent',
        element: <ProtectedRoute><RequestReagentPage /></ProtectedRoute>
      },
      {
        path: 'reagent-requests/:id/edit',
        element: <ProtectedRoute><EditReagentPage /></ProtectedRoute>
      },
      {
        path: 'my-reagents',
        element: <ProtectedRoute><MyReagentsPage /></ProtectedRoute>
      },
      {
        path: 'request-swap/:reagentId',
        element: <ProtectedRoute><SwapRequestPage /></ProtectedRoute>
      },
      {
        path: 'swap-request/:id',
        element: <ProtectedRoute><ViewSwapRequestPage /></ProtectedRoute>
      },
      {
        path: 'conversation/:userId',
        element: <ProtectedRoute><ConversationPage /></ProtectedRoute>
      },
      // Add support for the plural version of the conversation path
      {
        path: 'conversations/:userId',
        element: <ProtectedRoute><ConversationPage /></ProtectedRoute>
      },
      {
        path: 'messages',
        element: <ProtectedRoute><MessagesPage /></ProtectedRoute>
      },
      {
        path: 'admin',
        element: <ProtectedRoute><AdminPage /></ProtectedRoute>
      },
      // Add the main profile route without ID parameter
      {
        path: 'profile',
        element: <ProtectedRoute><ProfilePage /></ProtectedRoute>
      },
      {
        path: 'profile/:id',
        element: <ProtectedRoute><ProfilePage /></ProtectedRoute>
      },
      {
        path: 'profile/:id/edit',
        element: <ProtectedRoute><PublicProfilePage /></ProtectedRoute>
      },
      {
        path: 'login',
        element: <LoginPage />
      },
      {
        path: 'register',
        element: <RegisterPage />
      },
      // Auth routes for cleaner URLs
      {
        path: 'auth/login',
        element: <LoginPage />
      },
      {
        path: 'auth/register',
        element: <RegisterPage />
      },
      {
        path: 'pending-approval',
        element: <PendingApprovalPage />
      },
      {
        path: 'message/:reagentId',
        element: <ProtectedRoute><MessagePage /></ProtectedRoute>
      },
      // Add the community routes
      {
        path: 'community',
        element: <ProtectedRoute><CommunityPage /></ProtectedRoute>
      },
      {
        path: 'community/profile/:userId',
        element: <ProtectedRoute><PublicProfilePage /></ProtectedRoute>
      },
      // Add the help center route
      {
        path: 'help',
        element: <HelpCenterPage />
      },
      // Add the privacy policy route
      {
        path: 'privacy',
        element: <PrivacyPolicyPage />
      },
      // Add the terms and conditions route
      {
        path: 'terms',
        element: <TermsAndConditionsPage />
      },
      // Add the contact route
      {
        path: 'contact',
        element: <ContactPage />
      },
    ],
  },
]);

const AppWrapper: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      {/* Important: AuthProvider must wrap RouterProvider since components like Navbar use useAuth */}
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
      <ReactQueryDevtools initialIsOpen={false} />
      <Toaster />
    </QueryClientProvider>
  );
};

export default AppWrapper;
